(function () {
  if (typeof document === 'undefined') return;

  /**
   * Get quantity from quantity input component
   */
  function getQuantity(form) {
    const quantityInput = form?.querySelector('ck-quantity-input');
    if (!quantityInput) return 1;

    // Try internal input first
    const innerInput = quantityInput.querySelector('input');
    if (innerInput && innerInput.value != null) {
      const n = parseInt(innerInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }

    // Fallback: attribute or property on the host element
    const raw = (quantityInput.value ?? quantityInput.getAttribute('value') ?? '1') + '';
    const n = parseInt(raw, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  /**
   * Get selected variant ID from the hidden select
   * Falls back to first available variant if no selection
   */
  function getSelectedVariantId(form) {
    const selectEl = form?.querySelector('select[name="id"]');
    if (!selectEl) return null;

    // If a value is selected, use it
    if (selectEl.value) return selectEl.value;

    // Fallback: use first available option if select has options but no value selected
    if (selectEl.options.length > 0) {
      // Try to find first available variant
      for (let i = 0; i < selectEl.options.length; i++) {
        const option = selectEl.options[i];
        const isAvailable =
          option.dataset.available === 'true' || option.dataset.available === true;
        if (isAvailable) {
          return option.value;
        }
      }
      // If no available variant, use first option
      return selectEl.options[0].value;
    }

    return null;
  }

  /**
   * Handle "Buy it now" button click — build /cart/{variant}:{qty} using
   * the same quantity/variant reading logic as add-to-cart
   */
  function handleBuyNow(event) {
    const btn = event.target.closest('[data-buy-now-button]');
    if (!btn) return;

    event.preventDefault();

    const form = btn.closest('form');
    if (!form) return;

    const variantId = getSelectedVariantId(form) || btn.dataset.variantId;
    if (!variantId) return;

    const quantity = getQuantity(form);

    window.location.href = `/cart/${variantId}:${quantity}`;
  }

  /**
   * Get selling plan ID (subscription) from Appstle hidden input OR from PDP subscription UI
   * Falls back to reading from the checked subscription option's data-selling-plan-id
   * @param {HTMLElement} form - The product form element
   * @returns {string|null} Selling plan ID as string, or null if one-time
   */
  function getSellingPlanId(form) {
    // First, try to get from Appstle's hidden input (most reliable)
    const input = form?.querySelector('input[name="selling_plan"]');
    const inputValue = input?.value;

    if (inputValue && String(inputValue).trim() !== '') {
      return String(inputValue).trim();
    }

    // Fallback: read from PDP subscription UI's checked option
    // This handles cases where Appstle widget hasn't synced yet
    const subscriptionSection = form?.querySelector('[data-ck-subscription]');

    if (subscriptionSection) {
      // Check the actual radio input state (most reliable)
      const subscriptionRadio = subscriptionSection.querySelector(
        'input[type="radio"][name="subscription-option"][value="subscription"]'
      );
      const onetimeRadio = subscriptionSection.querySelector(
        'input[type="radio"][name="subscription-option"][value="onetime"]'
      );

      // Prefer radio input state (most reliable)
      if (subscriptionRadio && subscriptionRadio.checked) {
        // Find the subscription option card to get the plan ID
        const subscriptionOptionCard = subscriptionSection.querySelector(
          '[data-sub-option="subscription"][data-sub-option-card]'
        );

        if (subscriptionOptionCard) {
          const planId = subscriptionOptionCard.dataset.sellingPlanId;
          if (planId) {
            return String(planId);
          }
        }
      } else if (onetimeRadio && onetimeRadio.checked) {
        return null;
      }

      // Fallback to aria-checked attribute (less reliable but covers edge cases)
      const checkedByAria = subscriptionSection.querySelector(
        '[data-sub-option-card][aria-checked="true"]'
      );

      if (checkedByAria && checkedByAria.dataset.subOption === 'subscription') {
        const planId = checkedByAria.dataset.sellingPlanId;
        if (planId) {
          return String(planId);
        }
      }
    }

    return null;
  }

  /**
   * Get selected addons (warranty and installation)
   */
  function getSelectedAddons(form) {
    const addons = [];
    const checkboxes = form?.querySelectorAll('ck-checkbox[data-addon-variant-id]') || [];

    checkboxes.forEach((checkbox) => {
      const isChecked =
        checkbox.checked ||
        checkbox.hasAttribute('checked') ||
        checkbox.getAttribute('aria-checked') === 'true';

      if (isChecked) {
        const addonId = checkbox.getAttribute('data-addon-variant-id');
        if (addonId) addons.push({ id: addonId, quantity: 1 });
      }
    });

    return addons;
  }

  /** Properties (scoped to form) */
  function getProperties(form) {
    const properties = {};

    if (!form) return properties;

    form.querySelectorAll('input[name^="properties["]').forEach((input) => {
      const name = input.name.match(/properties\[(.+)\]/)?.[1];
      if (name && input.value) properties[name] = input.value;
    });

    return properties;
  }

  /**
   * Update cart count by calling the cart drawer's refresh function
   */
  function updateCartCount() {
    window.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /**
   * Format properties and variant info for display (similar to checkout.js)
   */
  function formatPropertiesAndVariants(properties, variantSelect) {
    const metafieldValues = [];
    const variantTitles = [];

    // Get variant title from select
    if (variantSelect && variantSelect.value) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (selectedOption && selectedOption.text) {
        const variantText = selectedOption.text.trim();
        // Skip "Default Title" variants
        if (
          variantText &&
          variantText !== 'Default Title' &&
          variantText.toLowerCase() !== 'default'
        ) {
          variantTitles.push(variantText);
        }
      }
    }

    // Extract metafield property values
    if (properties && typeof properties === 'object') {
      Object.entries(properties).forEach(([key, value]) => {
        // Skip empty values and variant title property
        if (key && value && String(value).trim() !== '' && key.toLowerCase() !== 'variant title') {
          metafieldValues.push(String(value).trim());
        }
      });
    }

    // Combine metafield values and variant titles
    const allDetails = [...metafieldValues, ...variantTitles];
    return allDetails.length > 0 ? allDetails.join(', ') : '';
  }

  /**
   * Get product information from form for toast notification
   */
  function getProductInfoForToast(form) {
    if (!form) return null;

    // Try to get product title from form or nearby elements
    let productTitle = '';

    // Try data attribute first
    const productTitleEl = form.querySelector('[data-product-title]');
    if (productTitleEl) {
      productTitle =
        productTitleEl.getAttribute('data-product-title') ||
        productTitleEl.textContent?.trim() ||
        '';
    }

    // Try h1 in main content area (most common on PDP)
    if (!productTitle) {
      const main = form.closest('main') || document.querySelector('main');
      if (main) {
        const h1 = main.querySelector('h1');
        if (h1) productTitle = h1.textContent?.trim() || '';
      }
    }

    // Try product title in product info section
    if (!productTitle) {
      const productInfoSection = document.querySelector('[data-product-info]');
      if (productInfoSection) {
        const h1 = productInfoSection.querySelector('h1');
        if (h1) productTitle = h1.textContent?.trim() || '';
      }
    }

    // Try to get from product description snippet (contains h1 with product title)
    if (!productTitle) {
      // Look for h1 near the form (product description snippet)
      const formParent = form.closest('[class*="product"], main, .container, [class*="container"]');
      if (formParent) {
        const h1 = formParent.querySelector('h1');
        if (h1) productTitle = h1.textContent?.trim() || '';
      }
    }

    // Last resort: search entire document for h1 (PDP typically has one main h1)
    if (!productTitle) {
      const allH1s = document.querySelectorAll('h1');
      // Skip header/footer h1s, get the main product title
      for (const h1 of allH1s) {
        // Skip if it's in header or footer
        if (h1.closest('header, footer, [class*="header"], [class*="footer"]')) continue;
        // Skip if it's too small (likely not the main product title)
        const computedStyle = window.getComputedStyle(h1);
        if (parseInt(computedStyle.fontSize) < 20) continue;
        productTitle = h1.textContent?.trim() || '';
        if (productTitle) break;
      }
    }

    // Try to get product image from form or product media
    let productImage = '';

    // Try data attribute first
    const productImageEl = form.querySelector('[data-product-image]');
    if (productImageEl) {
      productImage = productImageEl.getAttribute('data-product-image') || productImageEl.src || '';
    }

    // Try to get from product media container (visible image)
    if (!productImage) {
      const mediaContainer = document.querySelector('[data-product-media-container]');
      if (mediaContainer) {
        // Get the first visible/active image
        const visibleImg = mediaContainer.querySelector(
          '[data-product-image-item] img:not([style*="display: none"])'
        );
        if (visibleImg) {
          productImage = visibleImg.src || '';
        } else {
          // Fallback to first image
          const firstImg = mediaContainer.querySelector('[data-product-image-item] img');
          if (firstImg) productImage = firstImg.src || '';
        }
      }
    }

    // Try to get from variant image mapping (ProductVariantImagesJson)
    if (!productImage) {
      const variantId = getSelectedVariantId(form);
      if (variantId) {
        const productId = form.getAttribute('data-product-id');
        if (productId) {
          try {
            const jsonEl = document.getElementById(`ProductVariantImagesJson-${productId}`);
            if (jsonEl) {
              const variantImages = JSON.parse(jsonEl.textContent || '{}');
              const variantImage = variantImages[variantId];
              if (variantImage && variantImage.length > 0) {
                // Get the image URL - might need to construct it
                const imageId = variantImage[0];
                const mediaContainer = document.querySelector('[data-product-media-container]');
                if (mediaContainer) {
                  const imgEl = mediaContainer.querySelector(`[data-image-id="${imageId}"] img`);
                  if (imgEl) productImage = imgEl.src || '';
                }
              }
            }
          } catch (e) {
            // Silently fail if JSON parsing fails
          }
        }
      }
    }

    // Extract variant and metafield info for description
    const variantSelect = form.querySelector('select[name="id"]');
    const properties = getProperties(form);
    const description = formatPropertiesAndVariants(properties, variantSelect);

    // If we have both title and image, return product info
    if (productTitle && productImage) {
      return {
        name: productTitle,
        image: productImage,
        ...(description ? { description } : {}),
      };
    }

    // If we only have title, still return it (toast will show as default type)
    return null;
  }

  /**
   * Handle add to cart button click
   */
  async function handleAddToCart(event) {
    const form = event.target.closest('form');
    if (!form) return;

    const button = form.querySelector('ck-button[type="submit"]');
    if (!button) return;

    if (button.disabled || button.hasAttribute('data-adding')) return;

    // Always prevent default to avoid page reload
    event.preventDefault();
    event.stopPropagation();

    const priceSpan = form.querySelector('[data-product-add-to-cart-price]');

    // Clean up any existing timers from previous attempts
    const existingResetTimer = button.dataset.resetTimerId;
    const existingDrawerTimer = button.dataset.drawerTimerId;
    if (existingResetTimer) clearTimeout(Number(existingResetTimer));
    if (existingDrawerTimer) clearTimeout(Number(existingDrawerTimer));

    const originalHTML = button.innerHTML;
    const originalWidth = button.style.width || getComputedStyle(button).width;

    button.setAttribute('data-adding', 'true');
    button.loading = true;
    button.disabled = true;

    // Store original width to maintain it during loading
    if (originalWidth && originalWidth !== 'auto') {
      button.style.width = originalWidth;
    }

    // Check if subscription is selected - Appstle needs to handle subscription application
    const sellingPlanId = getSellingPlanId(form);

    try {
      const variantId = getSelectedVariantId(form);
      if (!variantId) throw new Error('No variant selected');

      const quantity = getQuantity(form);
      const properties = getProperties(form);
      const addons = getSelectedAddons(form);

      // Build cart items payload
      // For subscriptions, submit via FormData to form action (Appstle intercepts)
      // For one-time, use Cart API directly
      if (sellingPlanId) {
        // Subscription selected - submit via FormData to form action URL
        // This allows Appstle to intercept and process the subscription correctly
        try {
          // Ensure Appstle input is synced if it exists
          const appstleInput = form?.querySelector('input[name="selling_plan"]');
          if (appstleInput && appstleInput.value !== sellingPlanId) {
            appstleInput.value = sellingPlanId;
          }

          // Build FormData from form (includes all Appstle inputs)
          const formData = new FormData(form);

          // Ensure variant ID and quantity are set
          formData.set('id', variantId);
          formData.set('quantity', String(quantity));

          // IMPORTANT: Explicitly set selling_plan in FormData
          // Appstle's hidden input might not exist yet, so we set it directly
          formData.set('selling_plan', sellingPlanId);

          // Add properties
          Object.entries(properties).forEach(([key, value]) => {
            formData.set(`properties[${key}]`, value);
          });

          // Get form action URL (usually /cart/add)
          const formAction = form.getAttribute('action') || '/cart/add';

          // Submit via form action - Appstle will intercept this
          const response = await fetch(formAction, {
            method: 'POST',
            body: formData,
          });

          // Check response - might be HTML redirect or JSON
          const contentType = response.headers.get('content-type') || '';
          const isJson = contentType.includes('application/json');

          if (!response.ok && !isJson) {
            throw new Error('Failed to add subscription item to cart');
          }

          // Store subscription selection in localStorage as fallback
          // Key: variant_id -> value: selling_plan_id
          // This helps sync UI if Shopify doesn't immediately apply subscription
          try {
            const storageKey = `subscription_plan_${variantId}`;
            localStorage.setItem(storageKey, sellingPlanId);
          } catch (e) {
            // localStorage might be disabled, silently fail
          }

          // Refresh cart to get updated state (Appstle should have applied subscription)
          updateCartCount();

          if (priceSpan) priceSpan.textContent = 'Added!';
          else button.innerHTML = 'Added!';

          // Get product info for product type toast
          const productInfo = getProductInfoForToast(form);
          if (productInfo) {
            window.ckToast?.success('addToCart', { product: productInfo });
          } else {
            // Fallback to default toast if product info not found
            window.ckToast?.success('addToCart');
          }

          // Open cart drawer
          const cartDrawer = document.getElementById('cart-drawer');
          const drawerTrigger = cartDrawer?.querySelector('ck-drawer-trigger');
          if (drawerTrigger) {
            const drawerTimerId = setTimeout(() => drawerTrigger.click(), 250);
            button.dataset.drawerTimerId = String(drawerTimerId);
          }

          // Reset button after delay
          const resetTimerId = setTimeout(() => {
            button.loading = false;
            button.innerHTML = originalHTML;
            button.removeAttribute('data-adding');
            button.disabled = false;
            button.style.width = '';
            delete button.dataset.resetTimerId;
          }, 500);
          button.dataset.resetTimerId = String(resetTimerId);

          return; // Exit early for subscription flow
        } catch (error) {
          console.error('[product-add-to-cart.js] Error adding subscription item:', error);
          throw error;
        }
      }

      // One-time purchase - use Cart API directly (AJAX)
      const items = [
        {
          id: variantId,
          quantity,
          properties: Object.keys(properties).length ? properties : undefined,
        },
        ...addons,
      ];

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[PDP add-to-cart] error status:', response.status, errorData);

        // Better error messages for inventory issues
        const errorMessage = errorData.description || errorData.message || 'Failed to add to cart';
        const lowerMessage = errorMessage.toLowerCase();

        // Check if it's an inventory/quantity issue
        if (
          response.status === 422 &&
          (lowerMessage.includes('inventory') ||
            lowerMessage.includes('quantity') ||
            lowerMessage.includes('stock') ||
            lowerMessage.includes('available'))
        ) {
          // Check if variant has inventory info in the form
          const variantSelect = form.querySelector('select[name="id"]');
          const selectedOption = variantSelect?.options[variantSelect.selectedIndex];
          const inventoryQuantity = selectedOption?.dataset?.inventoryQuantity;

          if (inventoryQuantity !== undefined) {
            const availableQty = window.utils?.normalizeInventoryQuantity
              ? window.utils.normalizeInventoryQuantity(inventoryQuantity)
              : Math.max(0, parseInt(inventoryQuantity, 10) || 0);
            if (quantity > availableQty) {
              throw new Error(
                `Only ${availableQty} ${availableQty === 1 ? 'item is' : 'items are'} available. Please reduce the quantity.`
              );
            }
          }
        }

        throw new Error(errorMessage);
      }

      await response.json().catch(() => null);

      updateCartCount();

      // ✅ only show “Added!” on success
      if (priceSpan) priceSpan.textContent = 'Added!';
      else button.innerHTML = 'Added!';

      window.ckToast?.success('addToCart');

      // Store timer ID for cleanup
      const resetTimerId = setTimeout(() => {
        button.loading = false;
        button.innerHTML = originalHTML;
        button.removeAttribute('data-adding');
        button.disabled = false;
        button.style.width = '';
        delete button.dataset.resetTimerId;
      }, 500);
      button.dataset.resetTimerId = String(resetTimerId);

      // Open cart drawer (optional)
      const cartDrawer = document.getElementById('cart-drawer');
      const drawerTrigger = cartDrawer?.querySelector('ck-drawer-trigger');
      if (drawerTrigger) {
        const drawerTimerId = setTimeout(() => {
          drawerTrigger.click();
          delete button.dataset.drawerTimerId;
        }, 250);
        button.dataset.drawerTimerId = String(drawerTimerId);
      }
    } catch (error) {
      console.error('[PDP add-to-cart] Error:', error);

      // Clean up any pending timers on error
      const resetTimerId = button.dataset.resetTimerId;
      const drawerTimerId = button.dataset.drawerTimerId;
      if (resetTimerId) {
        clearTimeout(Number(resetTimerId));
        delete button.dataset.resetTimerId;
      }
      if (drawerTimerId) {
        clearTimeout(Number(drawerTimerId));
        delete button.dataset.drawerTimerId;
      }

      window.ckToast?.error('addToCart', error);

      button.loading = false;
      button.innerHTML = originalHTML;
      button.removeAttribute('data-adding');
      button.disabled = false;
      button.style.width = '';
    }
  }

  /**
   * Initialize add to cart handler
   */
  function init() {
    // Attach to ALL product forms on the page (safer than “first button”)
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (!forms.length) {
      console.warn('No /cart/add forms found');
      return;
    }

    forms.forEach((form) => {
      if (form.hasAttribute('data-ck-atc-bound')) return;
      form.setAttribute('data-ck-atc-bound', 'true');

      // Handle form submission
      form.addEventListener('submit', handleAddToCart);

      // Handle "Buy it now" clicks
      form.addEventListener('click', handleBuyNow);

      // Handle clicks on ck-button submit buttons
      // ck-button components need special handling since they're custom elements
      form.addEventListener('click', (event) => {
        const btn = event.target.closest('ck-button[type="submit"]');
        if (!btn || !form.contains(btn)) return;

        // If button is disabled or already processing, ignore
        if (btn.disabled || btn.hasAttribute('data-adding')) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        // Prevent default browser form submission
        // We'll handle it programmatically to ensure our handler runs
        event.preventDefault();
        event.stopPropagation();

        // Directly call handleAddToCart with a synthetic submit event
        // This ensures our handler runs reliably
        const syntheticEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
          target: form,
          currentTarget: form,
        };
        handleAddToCart(syntheticEvent);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();